import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, MenuItem, TextField, Typography, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Divider, IconButton, Tooltip, ToggleButtonGroup, ToggleButton,
  useMediaQuery, useTheme, Stack
} from '@mui/material';
import { Download, Inventory2, Search, TableChart, InfoOutlined, Close,
  ArrowUpward, ArrowDownward, AccessTime, Refresh } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import axios from 'axios';
import { exportGenericWorkbook } from '../utils/excel/genericWorkbook';

const KardexPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [filtros, setFiltros] = useState({
    productoId: '',  productoObj:  null,
    almacenId:  '',  almacenObj:   null,
    sucursalId: '',  sucursalObj:  null,
    categoriaId:'',
    tipo: '',
    desde: new Date(new Date().setDate(1)).toISOString().slice(0, 10),
    hasta: new Date().toLocaleDateString('en-CA'),
  });

  const [movimientos, setMovimientos] = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [exportando,  setExportando]  = useState(false);
  const [vista,       setVista]       = useState('movimientos');
  const [stockData,   setStockData]   = useState([]);
  // Modo de costo para Stock Actual: 'kardex' (costo promedio ponderado real)
  // o 'ultima_venta' (precio unitario de la venta más reciente del producto).
  const [modoCosto,   setModoCosto]   = useState('kardex');
  const [dlgModoCostoAbierto, setDlgModoCostoAbierto] = useState(false);

  const [productos,   setProductos]   = useState([]);
  const [almacenes,   setAlmacenes]   = useState([]);
  const [sucursales,  setSucursales]  = useState([]);
  const [categorias,  setCategorias]  = useState([]);
  const [prodInput,   setProdInput]   = useState('');

  const [detalleOpen, setDetalleOpen] = useState(false);
  const [detalleRow,  setDetalleRow]  = useState(null);
  const [orden,          setOrden]          = useState('DESC');
  const [mostrarHora,    setMostrarHora]    = useState(false);
  const [recalculating,  setRecalculating]  = useState(false);
  const [recalcMsg,      setRecalcMsg]      = useState('');

  // ── Catálogos ───────────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get('/api/almacen/mis-almacenes').then(r => setAlmacenes(r.data || [])).catch(() => {});
    axios.get('/api/sucursales/mis-sucursales').then(r => setSucursales(r.data || [])).catch(() => {});
    axios.get('/api/categorias').then(r => setCategorias(r.data || [])).catch(() => {});
  }, []);

  const fetchProductos = useCallback(async (q) => {
    try {
      const res = await axios.get('/api/productos', {
        params: { search: q || '', limit: 40, afectaKardex: 1 }
      });
      setProductos(res.data?.data || res.data || []);
    } catch { setProductos([]); }
  }, []);

  useEffect(() => { fetchProductos(''); }, [fetchProductos]);

  // ── Datos ────────────────────────────────────────────────────────────────────
  const fetchMovimientos = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, limit: 50, orden,
        ...(filtros.productoId  && { productoId:  filtros.productoId }),
        ...(filtros.almacenId   && { almacenId:   filtros.almacenId }),
        ...(filtros.sucursalId  && { sucursalId:  filtros.sucursalId }),
        ...(filtros.desde       && { desde:        filtros.desde }),
        ...(filtros.hasta       && { hasta:        filtros.hasta }),
        ...(filtros.tipo        && { tipo:          filtros.tipo }),
      };
      const res = await axios.get('/api/kardex/movimientos', { params });
      setMovimientos(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch { setMovimientos([]); }
    setLoading(false);
  }, [filtros, page, orden]);

  const fetchStock = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...(filtros.almacenId   && { almacenId:   filtros.almacenId }),
        ...(filtros.sucursalId  && { sucursalId:  filtros.sucursalId }),
        ...(filtros.categoriaId && { categoriaId: filtros.categoriaId }),
        ...(filtros.productoId  && { productoId:  filtros.productoId }),
        soloConStock: 0,
        modoCosto,
      };
      const res = await axios.get('/api/kardex/reporte-stock', { params });
      setStockData(res.data || []);
    } catch { setStockData([]); }
    setLoading(false);
  }, [filtros, modoCosto]);

  useEffect(() => {
    if (vista === 'movimientos') fetchMovimientos();
    else fetchStock();
  }, [vista, fetchMovimientos, fetchStock]);

  const setF = (field, value) => { setFiltros(prev => ({ ...prev, [field]: value })); setPage(1); };
  const buscar = () => { if (vista === 'movimientos') fetchMovimientos(); else fetchStock(); };

  // ── Excel ───────────────────────────────────────────────────────────────────
  // Usa su propia bandera (exportando), separada de `loading` — antes reutilizaba
  // `loading`, que también controla el spinner de la lista en pantalla: generar
  // el Excel hacía que la lista visible se ocultara y "recargara" un instante,
  // aunque los datos ya cargados nunca cambiaban.
  const exportarExcel = async () => {
    setExportando(true);
    try {
      if (vista === 'movimientos') {
        const params = { page: 1, limit: 5000,
          ...(filtros.productoId  && { productoId:  filtros.productoId }),
          ...(filtros.almacenId   && { almacenId:   filtros.almacenId }),
          ...(filtros.sucursalId  && { sucursalId:  filtros.sucursalId }),
          ...(filtros.desde       && { desde:        filtros.desde }),
          ...(filtros.hasta       && { hasta:        filtros.hasta }),
          ...(filtros.tipo        && { tipo:          filtros.tipo }),
        };
        const res = await axios.get('/api/kardex/movimientos', { params });
        await exportGenericWorkbook({
          titulo: 'KARDEX DE INVENTARIO — MOVIMIENTOS',
          sheetName: 'Movimientos', tableName: 'tblKardexMov',
          fileName: `Kardex_Movimientos_${filtros.desde}_${filtros.hasta}`,
          columns: [
            { key: 'fecha',           label: 'Fecha',        width: 14 },
            { key: 'tipo_movimiento', label: 'Tipo',         width: 10 },
            { key: 'tipo_doc',        label: 'Tipo Doc.',    width: 12 },
            { key: 'documento_num',   label: 'Documento',    width: 20 },
            { key: 'doc_legal_num',   label: 'Doc. Legal',   width: 20 },
            { key: 'auxiliar_doc',    label: 'RUC/DNI',      width: 14 },
            { key: 'auxiliar_nombre', label: 'Razón Social', width: 32 },
            { key: 'producto_nombre', label: 'Producto',     width: 32 },
            { key: 'almacen_nombre',  label: 'Almacén',      width: 20 },
            { key: 'cantidad',        label: 'Cantidad',     width: 10 },
            { key: 'costo_unitario',  label: 'Costo Unit.',  width: 13 },
            { key: 'costo_total',     label: 'Costo Total',  width: 13 },
            { key: 'saldo_cantidad',  label: 'Saldo Cant.',  width: 13 },
            { key: 'saldo_costo_total',label:'Saldo Costo',  width: 13 },
            { key: 'usuario',         label: 'Usuario',      width: 15 },
          ],
          rows: (res.data.data || []).map(m => ({
            ...m,
            fecha:             m.fecha?.slice(0, 10) || '',
            documento_num:     m.documento_num || m.documento_referencia || '-',
            doc_legal_num:     m.doc_legal_num || '',
            cantidad:          parseFloat(m.cantidad || 0),
            costo_unitario:    parseFloat(m.costo_unitario || 0),
            costo_total:       parseFloat(m.costo_total || 0),
            saldo_cantidad:    parseFloat(m.saldo_cantidad || 0),
            saldo_costo_total: parseFloat(m.saldo_costo_total || 0),
          })),
        });
      } else {
        const params = {
          ...(filtros.almacenId   && { almacenId:   filtros.almacenId }),
          ...(filtros.sucursalId  && { sucursalId:  filtros.sucursalId }),
          ...(filtros.productoId  && { productoId:  filtros.productoId }),
          ...(filtros.categoriaId && { categoriaId: filtros.categoriaId }),
          soloConStock: 0,
          modoCosto,
        };
        const res = await axios.get('/api/kardex/reporte-stock', { params });
        const filasDetalle = (res.data || []).map(s => ({
          ...s,
          stock:            parseFloat(s.stock || 0),
          costo_promedio:   parseFloat(s.costo_promedio || 0),
          valor_inventario: parseFloat(s.valor_inventario || 0),
        }));

        // Columnas dinámicas por almacén: se arman a partir de los almacenes que
        // realmente aparecen en los datos de este momento — si mañana se agrega
        // o elimina un almacén, estas columnas se ajustan solas, sin tocar código.
        const almacenesUnicos = [...new Set(filasDetalle.map(s => s.almacen).filter(Boolean))].sort();

        // Hoja consolidada: una fila por producto, con el stock desglosado por
        // almacén (columnas dinámicas) y sumado en Stock Total. Costo promedio
        // ponderado (no promedio simple) para que valor_inventario = stock *
        // costo_promedio siga cuadrando en la fila consolidada.
        const porProducto = new Map();
        filasDetalle.forEach(s => {
          const acc = porProducto.get(s.producto_id) || {
            producto: s.producto, codigo: s.codigo, categoria: s.categoria, marca: s.marca || '',
            stock: 0, valor_inventario: 0, porAlmacen: {}, costoUltimaVenta: s.costo_promedio,
          };
          acc.stock += s.stock;
          acc.valor_inventario += s.valor_inventario;
          if (s.almacen) acc.porAlmacen[s.almacen] = (acc.porAlmacen[s.almacen] || 0) + s.stock;
          porProducto.set(s.producto_id, acc);
        });
        const filasConsolidadas = Array.from(porProducto.values()).map(r => {
          const fila = { producto: r.producto, codigo: r.codigo, categoria: r.categoria, marca: r.marca };
          almacenesUnicos.forEach(alm => { fila[alm] = r.porAlmacen[alm] || 0; });
          fila.stock = r.stock;
          // "Última venta" es la misma para todas las filas del producto (no se
          // pondera por almacén); "kardex" sí necesita reconstruirse como
          // promedio ponderado para que valor_inventario = stock * costo_promedio cuadre.
          fila.costo_promedio = modoCosto === 'ultima_venta'
            ? r.costoUltimaVenta
            : (r.stock !== 0 ? r.valor_inventario / r.stock : 0);
          fila.valor_inventario = r.valor_inventario;
          return fila;
        });

        await exportGenericWorkbook({
          titulo: 'KARDEX — REPORTE STOCK ACTUAL', sheetName: 'Stock Actual',
          tableName: 'tblKardexStock', fileName: `Kardex_Stock_${new Date().toLocaleDateString('en-CA')}`,
          columns: [
            { key: 'producto',         label: 'Producto',         width: 32 },
            { key: 'codigo',           label: 'Código',           width: 14 },
            { key: 'categoria',        label: 'Categoría',        width: 18 },
            { key: 'marca',            label: 'Marca',            width: 16 },
            { key: 'almacen',          label: 'Almacén',          width: 20 },
            { key: 'sucursal',         label: 'Sucursal',         width: 18 },
            { key: 'stock',            label: 'Stock',            width: 10 },
            { key: 'costo_promedio',   label: modoCosto === 'ultima_venta' ? 'Últ. Venta' : 'Costo Prom.', width: 13 },
            { key: 'valor_inventario', label: 'Valor Inventario', width: 16 },
          ],
          rows: filasDetalle,
          agrupadoPorCampo: {
            sheetName: 'Stock Consolidado',
            columns: [
              { key: 'producto', label: 'Producto', width: 32 },
              { key: 'codigo',   label: 'Código',   width: 14 },
              { key: 'categoria',label: 'Categoría',width: 18 },
              { key: 'marca',    label: 'Marca',    width: 16 },
              ...almacenesUnicos.map(alm => ({ key: alm, label: alm, width: 14, sumTotal: true })),
              { key: 'stock',            label: 'Stock Total',      width: 12, sumTotal: true },
              { key: 'costo_promedio',   label: modoCosto === 'ultima_venta' ? 'Últ. Venta' : 'Costo Prom.', width: 13 },
              { key: 'valor_inventario', label: 'Valor Inventario', width: 16, sumTotal: true },
            ],
            grupos: [{
              titulo: 'Stock consolidado — suma de todos los almacenes/sucursales por producto',
              rows: filasConsolidadas,
            }],
          },
        });
      }
    } catch (e) { console.error('Excel error:', e); }
    setExportando(false);
  };

  const handleRecalcular = async () => {
    if (!window.confirm('¿Recalcular todos los saldos del Kardex? Esta operación puede tardar unos segundos.')) return;
    setRecalculating(true);
    setRecalcMsg('');
    try {
      const res = await axios.post('/api/kardex/recalcular');
      setRecalcMsg(`✓ Recalculado: ${res.data.actualizados} movimientos en ${res.data.combos} combinaciones producto/almacén.`);
      fetchMovimientos();
    } catch (e) {
      setRecalcMsg(`Error: ${e.response?.data?.error || e.message}`);
    }
    setRecalculating(false);
  };

  const tipoColor = (t) => t === 'ENTRADA' ? 'success' : 'error';

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: recalcMsg ? 1 : 2, gap: 1, flexWrap: 'wrap' }}>
        <Inventory2 color="primary" />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Kardex de Inventario</Typography>
        <Button variant={vista === 'movimientos' ? 'contained' : 'outlined'} size="small"
          onClick={() => setVista('movimientos')} startIcon={<TableChart />}>{isMobile ? 'Mov.' : 'Movimientos'}</Button>
        <Button variant={vista === 'stock' ? 'contained' : 'outlined'} size="small"
          onClick={() => setDlgModoCostoAbierto(true)} startIcon={<Inventory2 />}>Stock</Button>
        <Tooltip title="Recalcula saldos de todos los productos (útil si el orden de registros fue modificado)">
          <span>
            <Button variant="outlined" size="small" color="warning"
              onClick={handleRecalcular} disabled={recalculating}
              startIcon={<Refresh />}>
              {recalculating ? '...' : (isMobile ? 'Recalc.' : 'Recalcular')}
            </Button>
          </span>
        </Tooltip>
      </Box>
      {recalcMsg && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1.5,
          color: recalcMsg.startsWith('✓') ? 'success.dark' : 'error.main' }}>
          {recalcMsg}
        </Typography>
      )}

      {/* Filtros */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', gap: 1.5, alignItems: isMobile ? 'stretch' : 'center' }}>

          {/* 1. Sucursal */}
          <Autocomplete size="small" sx={{ minWidth: isMobile ? '100%' : 170, flex: isMobile ? 'none' : '1 1 170px' }}
            options={sucursales}
            getOptionLabel={o => o.nombre || ''}
            value={filtros.sucursalObj}
            onChange={(_, v) => {
              setF('sucursalId', v?.id || '');
              setF('sucursalObj', v);
              setF('almacenId', '');
              setF('almacenObj', null);
            }}
            renderInput={p => <TextField {...p} label="Sucursal" />}
            isOptionEqualToValue={(o, v) => o.id === v?.id}
          />

          {/* 2. Almacén */}
          <Autocomplete size="small" sx={{ minWidth: isMobile ? '100%' : 170, flex: isMobile ? 'none' : '1 1 170px' }}
            options={almacenes.filter(a => !filtros.sucursalId || a.sucursal_id === filtros.sucursalId)}
            getOptionLabel={o => o.nombre || ''}
            value={filtros.almacenObj}
            onChange={(_, v) => { setF('almacenId', v?.id || ''); setF('almacenObj', v); }}
            renderInput={p => <TextField {...p} label="Almacén" />}
            isOptionEqualToValue={(o, v) => o.id === v?.id}
          />

          {/* 3. Producto */}
          <Autocomplete size="small" sx={{ minWidth: isMobile ? '100%' : 240, flex: isMobile ? 'none' : '2 1 240px' }}
            options={productos}
            filterOptions={x => x}
            getOptionLabel={o => `${o.codigo ? o.codigo + ' - ' : ''}${o.nombre || ''}`}
            value={filtros.productoObj}
            inputValue={prodInput}
            onInputChange={(_, v) => { setProdInput(v); if (v.length === 0 || v.length >= 2) fetchProductos(v); }}
            onChange={(_, v) => { setF('productoId', v?.id || ''); setF('productoObj', v); }}
            renderInput={p => <TextField {...p} label="Producto" />}
            isOptionEqualToValue={(o, v) => o.id === v?.id}
            noOptionsText="Sin resultados"
          />

          {/* 4. Categoría */}
          <TextField select label="Categoría" size="small" sx={{ minWidth: isMobile ? '100%' : 160 }}
            value={filtros.categoriaId}
            onChange={e => setF('categoriaId', e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            {categorias.filter(c => c.afecta_kardex).map(c => (
              <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
            ))}
          </TextField>

          {/* 5. Tipo */}
          <TextField select label="Tipo" size="small" sx={{ minWidth: isMobile ? '100%' : 130 }}
            value={filtros.tipo}
            onChange={e => setF('tipo', e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="ENTRADA">Entradas</MenuItem>
            <MenuItem value="SALIDA">Salidas</MenuItem>
          </TextField>

          {/* 6. Fechas */}
          {isMobile ? (
            <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
              <TextField label="Desde" type="date" size="small" sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }} value={filtros.desde}
                onChange={e => setF('desde', e.target.value)} />
              <TextField label="Hasta" type="date" size="small" sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }} value={filtros.hasta}
                onChange={e => setF('hasta', e.target.value)} />
            </Box>
          ) : (
            <>
              <TextField label="Desde" type="date" size="small" sx={{ minWidth: 135 }}
                InputLabelProps={{ shrink: true }} value={filtros.desde}
                onChange={e => setF('desde', e.target.value)} />
              <TextField label="Hasta" type="date" size="small" sx={{ minWidth: 135 }}
                InputLabelProps={{ shrink: true }} value={filtros.hasta}
                onChange={e => setF('hasta', e.target.value)} />
            </>
          )}

          <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto' }}>
            <Button variant="contained" size="small" onClick={buscar}
              startIcon={<Search />} disabled={loading} sx={{ flex: isMobile ? 1 : 'none' }}>Buscar</Button>
            <Button variant="outlined" size="small" color="success"
              onClick={exportarExcel} startIcon={<Download />} disabled={exportando} sx={{ flex: isMobile ? 1 : 'none' }}>
              {exportando ? 'Generando...' : 'Excel'}
            </Button>
          </Box>

          {vista === 'movimientos' && (
            <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'center' : 'flex-start' }}>
              <ToggleButtonGroup size="small" value={orden} exclusive
                onChange={(_, v) => { if (v) { setOrden(v); setPage(1); } }}>
                <ToggleButton value="DESC" title="Más reciente primero">
                  <ArrowDownward fontSize="small" />
                </ToggleButton>
                <ToggleButton value="ASC" title="Más antiguo primero">
                  <ArrowUpward fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title={mostrarHora ? 'Ocultar hora' : 'Mostrar hora'}>
                <ToggleButton size="small" value="hora" selected={mostrarHora}
                  onChange={() => setMostrarHora(v => !v)}
                  sx={{ border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1 }}>
                  <AccessTime fontSize="small" />
                </ToggleButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Vista Movimientos */}
      {vista === 'movimientos' && (
        <>
          {isMobile ? (
            <Stack spacing={1}>
              {loading ? (
                <Typography align="center" color="text.secondary" sx={{ py: 3 }}>Cargando...</Typography>
              ) : movimientos.length === 0 ? (
                <Typography align="center" color="text.secondary" sx={{ py: 3 }}>Sin movimientos en el período</Typography>
              ) : movimientos.map(m => (
                <Paper key={m.id} elevation={1} sx={{ p: 1.5, borderLeft: 4, borderColor: m.tipo_movimiento === 'ENTRADA' ? 'success.main' : 'error.main' }}
                  onClick={() => { setDetalleRow(m); setDetalleOpen(true); }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{m.producto_nombre}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {mostrarHora ? m.fecha?.slice(0,19).replace('T',' ') : m.fecha?.slice(0,10)} · {m.almacen_nombre}
                      </Typography>
                    </Box>
                    <Chip label={m.tipo_movimiento} size="small" color={tipoColor(m.tipo_movimiento)} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {m.documento_num || m.documento_referencia || '-'}
                      </Typography>
                      {m.doc_legal_num && (
                        <Chip label={m.doc_legal_num} size="small"
                          color={m.doc_legal_tipo === 'FACTURA' ? 'primary' : 'success'}
                          sx={{ fontSize: '0.65rem', height: 18, mt: 0.3 }} />
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body1" fontWeight={700}>{parseFloat(m.cantidad||0).toFixed(2)}</Typography>
                      <Typography variant="caption" color={parseFloat(m.saldo_cantidad||0)<=0?'error.main':'text.secondary'}>
                        Saldo: {parseFloat(m.saldo_cantidad||0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
          <TableContainer component={Paper} elevation={2}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  {[mostrarHora ? 'Fecha y Hora' : 'Fecha','Tipo','Tipo Doc.','Documento','Doc. Legal','RUC/DNI','Razón Social',
                    'Producto','Almacén','Cantidad','Costo Unit.',
                    'Costo Total','Saldo Cant.','Saldo Costo','Usuario',''].map(h => (
                    <TableCell key={h} sx={{ color:'white', fontWeight:'bold', whiteSpace:'nowrap', py:1 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={16} align="center">Cargando...</TableCell></TableRow>
                ) : movimientos.length === 0 ? (
                  <TableRow><TableCell colSpan={16} align="center" sx={{ color:'text.secondary', py:3 }}>Sin movimientos en el período</TableCell></TableRow>
                ) : movimientos.map((m, i) => (
                  <TableRow key={m.id} hover
                    sx={{ bgcolor: i%2===0?'grey.50':'white', cursor:'pointer' }}
                    onDoubleClick={() => { setDetalleRow(m); setDetalleOpen(true); }}>
                    <TableCell sx={{ whiteSpace:'nowrap' }}>
                      {mostrarHora
                        ? m.fecha?.slice(0,19).replace('T',' ')
                        : m.fecha?.slice(0,10)}
                    </TableCell>
                    <TableCell>
                      <Chip label={m.tipo_movimiento} size="small" color={tipoColor(m.tipo_movimiento)} />
                    </TableCell>
                    <TableCell sx={{ fontSize:'0.75rem', color:'text.secondary' }}>{m.tipo_doc || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace:'nowrap', fontWeight:600, color:'primary.dark' }}>
                      {m.documento_num || m.documento_referencia || '-'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace:'nowrap' }}>
                      {m.doc_legal_num ? (
                        <Tooltip title={`${m.doc_legal_tipo} — ${m.doc_legal_fecha ? String(m.doc_legal_fecha).slice(0,10) : ''}`}>
                          <Chip label={m.doc_legal_num} size="small"
                            color={m.doc_legal_tipo === 'FACTURA' ? 'primary' : 'success'}
                            sx={{ fontSize:'0.7rem', height:20 }} />
                        </Tooltip>
                      ) : (m.tipo_doc === 'NV' ? (
                        <Typography variant="caption" color="text.disabled">sin CPE</Typography>
                      ) : null)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace:'nowrap', fontSize:'0.75rem' }}>{m.auxiliar_doc || '-'}</TableCell>
                    <TableCell sx={{ maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                      title={m.auxiliar_nombre}>{m.auxiliar_nombre || '-'}</TableCell>
                    <TableCell sx={{ maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                      title={m.producto_nombre}>{m.producto_nombre}</TableCell>
                    <TableCell sx={{ fontSize:'0.75rem' }}>{m.almacen_nombre}</TableCell>
                    <TableCell align="right">{parseFloat(m.cantidad||0).toFixed(2)}</TableCell>
                    <TableCell align="right">{parseFloat(m.costo_unitario||0).toFixed(4)}</TableCell>
                    <TableCell align="right">{parseFloat(m.costo_total||0).toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight:'bold', color: parseFloat(m.saldo_cantidad||0)<=0?'error.main':'inherit' }}>
                      {parseFloat(m.saldo_cantidad||0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">{parseFloat(m.saldo_costo_total||0).toFixed(2)}</TableCell>
                    <TableCell sx={{ fontSize:'0.75rem' }}>{m.usuario||'-'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalle">
                        <IconButton size="small" onClick={() => { setDetalleRow(m); setDetalleOpen(true); }}>
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          <Box sx={{ mt:1, display:'flex', justifyContent: isMobile ? 'space-between' : 'flex-end', gap:1, alignItems:'center' }}>
            <Typography variant="caption" color="text.secondary">Total: {total} movimientos</Typography>
            <Button size="small" disabled={page<=1} onClick={() => setPage(p=>p-1)}>Anterior</Button>
            <Typography variant="caption">Pág. {page}</Typography>
            <Button size="small" disabled={movimientos.length<50} onClick={() => setPage(p=>p+1)}>Siguiente</Button>
          </Box>
        </>
      )}

      {/* Vista Stock */}
      {vista === 'stock' && (
        isMobile ? (
          <Stack spacing={1}>
            {loading ? (
              <Typography align="center" color="text.secondary" sx={{ py: 3 }}>Cargando...</Typography>
            ) : stockData.length === 0 ? (
              <Typography align="center" color="text.secondary" sx={{ py: 3 }}>Sin datos de stock</Typography>
            ) : stockData.map((s, i) => (
              <Paper key={i} elevation={1} sx={{ p: 1.5, borderLeft: 4, borderColor: parseFloat(s.stock) <= 0 ? 'error.main' : 'success.main' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{s.producto}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {s.codigo} · {s.categoria}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {s.almacen} — {s.sucursal}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="body1" fontWeight={700} color={parseFloat(s.stock) <= 0 ? 'error.dark' : 'inherit'}>
                      {parseFloat(s.stock||0).toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">stock</Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">{modoCosto === 'ultima_venta' ? 'Últ. Venta' : 'Costo Prom.'}: S/ {parseFloat(s.costo_promedio||0).toFixed(4)}</Typography>
                  <Typography variant="caption" fontWeight={600}>Valor: S/ {parseFloat(s.valor_inventario||0).toFixed(2)}</Typography>
                </Box>
              </Paper>
            ))}
          </Stack>
        ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor:'primary.main' }}>
                {['Producto','Código','Categoría','Almacén','Sucursal','Stock', modoCosto === 'ultima_venta' ? 'Últ. Venta' : 'Costo Prom.','Valor Inventario'].map(h=>(
                  <TableCell key={h} sx={{ color:'white', fontWeight:'bold' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center">Cargando...</TableCell></TableRow>
              ) : stockData.length===0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ color:'text.secondary', py:3 }}>Sin datos de stock</TableCell></TableRow>
              ) : stockData.map((s,i)=>(
                <TableRow key={i} hover sx={{ bgcolor:parseFloat(s.stock)<=0?'error.light':i%2===0?'grey.50':'white' }}>
                  <TableCell>{s.producto}</TableCell>
                  <TableCell>{s.codigo}</TableCell>
                  <TableCell>{s.categoria}</TableCell>
                  <TableCell>{s.almacen}</TableCell>
                  <TableCell>{s.sucursal}</TableCell>
                  <TableCell align="right" sx={{ fontWeight:'bold', color:parseFloat(s.stock)<=0?'error.dark':'inherit' }}>
                    {parseFloat(s.stock||0).toFixed(2)}
                  </TableCell>
                  <TableCell align="right">S/ {parseFloat(s.costo_promedio||0).toFixed(4)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight:'bold' }}>S/ {parseFloat(s.valor_inventario||0).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )
      )}

      {/* Dialog Detalle Movimiento */}
      <Dialog open={detalleOpen} onClose={()=>setDetalleOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{ sx:{ borderRadius: isMobile ? 0 : 3 } }}>
        <DialogTitle sx={{ bgcolor:'primary.main', color:'white', py:1.5, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Detalle del Movimiento</Typography>
            <Typography variant="caption" sx={{ opacity:0.85 }}>
              {detalleRow?.documento_num || detalleRow?.documento_referencia || ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={()=>setDetalleOpen(false)} sx={{ color:'white' }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt:2 }}>
          {detalleRow && (
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Fecha</Typography>
                <Typography fontWeight={600}>{detalleRow.fecha?.slice(0,10)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Tipo movimiento</Typography>
                <Box><Chip label={detalleRow.tipo_movimiento} size="small" color={tipoColor(detalleRow.tipo_movimiento)} /></Box>
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Tipo documento</Typography>
                <Typography fontWeight={600}>{detalleRow.tipo_doc || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Nº Documento</Typography>
                <Typography fontWeight={700} color="primary.dark">{detalleRow.documento_num || detalleRow.documento_referencia || '-'}</Typography>
              </Grid>
              {detalleRow.doc_legal_num && (
                <>
                  <Grid item xs={12}><Divider /></Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Doc. Legal vinculado</Typography>
                    <Typography fontWeight={700} color={detalleRow.doc_legal_tipo === 'FACTURA' ? 'primary.dark' : 'success.dark'}>
                      {detalleRow.doc_legal_num}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{detalleRow.doc_legal_tipo}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Fecha CPE</Typography>
                    <Typography fontWeight={600}>{detalleRow.doc_legal_fecha ? String(detalleRow.doc_legal_fecha).slice(0,10) : '-'}</Typography>
                  </Grid>
                </>
              )}
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={5}>
                <Typography variant="caption" color="text.secondary">RUC / DNI</Typography>
                <Typography fontWeight={600}>{detalleRow.auxiliar_doc || '-'}</Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography variant="caption" color="text.secondary">Razón Social / Nombre</Typography>
                <Typography fontWeight={600}>{detalleRow.auxiliar_nombre || '-'}</Typography>
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Producto</Typography>
                <Typography fontWeight={600}>{detalleRow.producto_nombre}</Typography>
                {detalleRow.producto_codigo && <Typography variant="caption" color="text.secondary">Cód: {detalleRow.producto_codigo}</Typography>}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Almacén</Typography>
                <Typography>{detalleRow.almacen_nombre}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Sucursal</Typography>
                <Typography>{detalleRow.sucursal_nombre || '-'}</Typography>
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Cantidad</Typography>
                <Typography fontWeight={700} fontSize="1.1rem">{parseFloat(detalleRow.cantidad||0).toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Costo Unitario</Typography>
                <Typography>S/ {parseFloat(detalleRow.costo_unitario||0).toFixed(4)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Costo Total</Typography>
                <Typography fontWeight={600}>S/ {parseFloat(detalleRow.costo_total||0).toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Saldo Cantidad</Typography>
                <Typography fontWeight={700} color={parseFloat(detalleRow.saldo_cantidad||0)<=0?'error.main':'success.dark'}>
                  {parseFloat(detalleRow.saldo_cantidad||0).toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Saldo Costo Total</Typography>
                <Typography fontWeight={600}>S/ {parseFloat(detalleRow.saldo_costo_total||0).toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Usuario</Typography>
                <Typography>{detalleRow.usuario || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Observaciones</Typography>
                <Typography variant="body2" color="text.secondary">{detalleRow.observaciones || '-'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDetalleOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Elegir qué costo mostrar en Stock Actual: costo real de kardex, o
          precio de la última venta del producto */}
      <Dialog open={dlgModoCostoAbierto} onClose={() => setDlgModoCostoAbierto(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', py: 1.5 }}>Costo a mostrar en Stock</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ¿Qué costo quieres ver en la columna "Costo Prom." del reporte de Stock Actual?
          </Typography>
          <Stack spacing={1.5}>
            <Button
              variant={modoCosto === 'kardex' ? 'contained' : 'outlined'}
              fullWidth
              sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.2 }}
              onClick={() => { setModoCosto('kardex'); setVista('stock'); setDlgModoCostoAbierto(false); }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>Costo Real (Kardex)</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>Costo promedio ponderado calculado por kardex</Typography>
              </Box>
            </Button>
            <Button
              variant={modoCosto === 'ultima_venta' ? 'contained' : 'outlined'}
              fullWidth
              sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.2 }}
              onClick={() => { setModoCosto('ultima_venta'); setVista('stock'); setDlgModoCostoAbierto(false); }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>Precio Última Venta</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>Precio unitario de la venta más reciente del producto</Typography>
              </Box>
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgModoCostoAbierto(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KardexPage;
